// NotificationCenter — pen frame TnT1r (浮层-代理通知中心)
//
// 复用 user 端 messaging endpoint (/gapi/list_my_conversations + /gapi/mark_messages_read);
// agent 自助场景下只读自己的会话(系统消息 + 客服回复)。
//
// UI 仿 pen.b2lst:头部"通知"+ 列表(每行:avatar/标题/时间/未读 badge);点单行调
// markMessagesRead 并跳 ticket 详情(待 §A5 接入 ticket SPA);此处暂只标已读。

import { useState, useRef, useEffect } from 'react'
import { Bell } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useMyConversations, markMessagesRead, type ConversationRow } from '../_lib/agent-api'
import { formatDate } from '@dogdisk/shared'

export function NotificationCenter() {
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useMyConversations()

  const convs = data?.conversations ?? []
  const totalUnread = convs.reduce((sum, c) => sum + (c.unread_count ?? 0), 0)

  // 点外部关闭
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const handleClick = async (c: ConversationRow) => {
    if ((c.unread_count ?? 0) > 0) {
      try {
        await markMessagesRead(c.conv_id)
        void qc.invalidateQueries({ queryKey: ['agent', 'notifications'] })
      } catch {
        /* swallow — popover 内部不展示错误 */
      }
    }
  }

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
        aria-label="通知"
      >
        <Bell size={18} />
        {totalUnread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ backgroundColor: '#EF4444' }}
            aria-label={`${totalUnread} 条未读`}
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="TnT1r"
          data-frame-id="TnT1r"
          className="absolute right-0 top-full z-50 mt-2 w-[360px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        >
          <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <span className="text-sm font-semibold text-gray-900">通知中心</span>
            <span className="text-xs text-gray-400">{convs.length} 个会话</span>
          </header>
          <div className="max-h-[480px] overflow-y-auto">
            {isLoading ? (
              <p className="px-4 py-8 text-center text-xs text-gray-400">加载中…</p>
            ) : convs.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-gray-400">暂无通知</p>
            ) : (
              <ul className="flex flex-col">
                {convs.map((c) => (
                  <li
                    key={c.conv_id}
                    className="flex cursor-pointer items-start gap-3 border-b border-gray-50 px-4 py-3 hover:bg-gray-50 last:border-b-0"
                    onClick={() => void handleClick(c)}
                  >
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: '#6366F1' }}
                    >
                      {(c.partner_username ?? '系').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-[13px] font-medium text-gray-900">
                          {c.partner_username ?? '系统通知'}
                        </span>
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {c.last_message_at ? formatDate(c.last_message_at) : '—'}
                        </span>
                      </div>
                      <p className="truncate text-xs text-gray-500">
                        {c.last_message ?? '(无消息)'}
                      </p>
                    </div>
                    {(c.unread_count ?? 0) > 0 && (
                      <span
                        className="ml-2 mt-1 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white"
                        style={{ backgroundColor: '#EF4444' }}
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
